import React, { useState, useRef } from 'react';
import type { IconComponent } from '../../icons';
import { CheckIcon, PencilIcon, ShareIcon, TrashIcon } from '../../icons';

interface SwipeAction {
  id: string;
  label: string;
  /** 아이콘 컴포넌트(`components/icons`). 문자열 글리프는 받지 않는다. */
  icon?: IconComponent;
  color: string; // Tailwind bg color class
  onClick: () => void;
}

interface SwipeableCardProps {
  /** 카드 내용 */
  children: React.ReactNode;
  /** 왼쪽 스와이프 액션 (오른쪽에 표시) */
  leftActions?: SwipeAction[];
  /** 오른쪽 스와이프 액션 (왼쪽에 표시) */
  rightActions?: SwipeAction[];
  /** 스와이프 비활성화 */
  disabled?: boolean;
  /** 스와이프 시작 임계값 (px) */
  threshold?: number;
}

export function SwipeableCard({
  children,
  leftActions = [],
  rightActions = [],
  disabled = false,
  threshold = 50,
}: SwipeableCardProps) {
  const [offset, setOffset] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // 액션 버튼 너비 계산
  const leftActionsWidth = leftActions.length * 80;
  const rightActionsWidth = rightActions.length * 80;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = startXRef.current;
    setIsActive(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabled || !isActive) return;

    currentXRef.current = e.touches[0].clientX;
    const diff = currentXRef.current - startXRef.current;

    // 왼쪽 스와이프 제한 (rightActions 보여주기)
    if (diff < 0 && rightActions.length === 0) return;
    // 오른쪽 스와이프 제한 (leftActions 보여주기)
    if (diff > 0 && leftActions.length === 0) return;

    // 최대 스와이프 거리 제한
    const maxOffset = diff > 0 ? leftActionsWidth : -rightActionsWidth;
    const limitedOffset = diff > 0
      ? Math.min(diff, maxOffset)
      : Math.max(diff, maxOffset);

    setOffset(limitedOffset);
  };

  const handleTouchEnd = () => {
    if (disabled) return;
    setIsActive(false);

    // 스냅 결정
    if (Math.abs(offset) > threshold) {
      // 열린 상태로 유지
      if (offset > 0) {
        setOffset(leftActionsWidth);
      } else {
        setOffset(-rightActionsWidth);
      }
    } else {
      // 닫기
      setOffset(0);
    }
  };

  const handleActionClick = (action: SwipeAction) => {
    action.onClick();
    setOffset(0); // 액션 실행 후 닫기
  };

  const closeSwipe = () => {
    setOffset(0);
  };

  return (
    <div className="relative overflow-hidden">
      {/* 왼쪽 액션 버튼들 (오른쪽 스와이프 시 표시) */}
      {leftActions.length > 0 && (
        <div
          className="absolute left-0 top-0 bottom-0 flex"
          style={{ width: leftActionsWidth }}
        >
          {leftActions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => handleActionClick(action)}
              className={`flex-1 flex flex-col items-center justify-center text-white ${action.color}`}
            >
              {action.icon && <action.icon className="h-5 w-5 mb-1" />}
              <span className="text-xs">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* 오른쪽 액션 버튼들 (왼쪽 스와이프 시 표시) */}
      {rightActions.length > 0 && (
        <div
          className="absolute right-0 top-0 bottom-0 flex"
          style={{ width: rightActionsWidth }}
        >
          {rightActions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => handleActionClick(action)}
              className={`flex-1 flex flex-col items-center justify-center text-white ${action.color}`}
            >
              {action.icon && <action.icon className="h-5 w-5 mb-1" />}
              <span className="text-xs">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* 카드 내용 */}
      <div
        ref={cardRef}
        className={`relative bg-white ${isActive ? '' : 'transition-transform duration-200'}`}
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={offset !== 0 ? closeSwipe : undefined}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * 미리 정의된 스와이프 액션
 */
export const SwipeActions = {
  delete: (onClick: () => void): SwipeAction => ({
    id: 'delete',
    label: '삭제',
    icon: TrashIcon,
    color: 'bg-red-500',
    onClick,
  }),

  resolve: (onClick: () => void): SwipeAction => ({
    id: 'resolve',
    label: '해결',
    icon: CheckIcon,
    color: 'bg-green-500',
    onClick,
  }),

  edit: (onClick: () => void): SwipeAction => ({
    id: 'edit',
    label: '수정',
    icon: PencilIcon,
    color: 'bg-blue-500',
    onClick,
  }),

  share: (onClick: () => void): SwipeAction => ({
    id: 'share',
    label: '공유',
    icon: ShareIcon,
    color: 'bg-purple-500',
    onClick,
  }),
};

export default SwipeableCard;
